import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const STORAGE_KEY = 'irecommend-theme';
const CHOICES = ['light', 'dark', 'system'];

// Kept in sync with --nav-bg in index.css, so the iOS status bar and the Android
// address bar match the app rather than flashing the other theme.
const META_COLOR = { light: '#0D1B2A', dark: '#16212C' };

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStored = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return CHOICES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

// The inline script in index.html has already done this before first paint; this
// keeps it true for every later change.
const paint = (resolved) => {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_COLOR[resolved]);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStored);
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const stored = readStored();
    return stored === 'system' ? (prefersDark() ? 'dark' : 'light') : stored;
  });

  const apply = useCallback((choice) => {
    const resolved = choice === 'system' ? (prefersDark() ? 'dark' : 'light') : choice;
    setResolvedTheme(resolved);
    paint(resolved);
  }, []);

  const setTheme = useCallback(
    (choice) => {
      if (!CHOICES.includes(choice)) return;
      setThemeState(choice);
      try {
        localStorage.setItem(STORAGE_KEY, choice);
      } catch {
        /* private mode: the choice simply lasts for this session */
      }
      const root = document.documentElement;
      root.classList.add('theme-transition');
      apply(choice);
      setTimeout(() => root.classList.remove('theme-transition'), 220);
    },
    [apply],
  );

  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  // On "System", follow the device live while the app is open.
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme, apply]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
